import type { RepositoryIntelligenceSyncList } from '@ashniva/types';
import { Button, Card, EmptyState, SkeletonText, Table, type TableColumn } from '@ashniva/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';

import { formatDateTime } from '../../../shared/lib/format';
import { riKeys, useRequestSync, useRiSyncsQuery } from '../api';
import { shortCommit } from '../freshness';
import { SYNC_STATUS_LABELS, SYNC_TRIGGER_LABELS, failureReason } from '../ri-text';
import { usePollWindow } from '../use-poll-window';
import { useSubmissionKey } from '../use-submission-key';
import { ProblemNotice } from './ProblemNotice';

type Sync = RepositoryIntelligenceSyncList['items'][number];

const RUNNING = new Set(['queued', 'running']);

const columns: TableColumn<Sync>[] = [
  {
    key: 'when',
    header: 'Started',
    render: (sync) => formatDateTime(sync.startedAt),
    nowrap: true,
  },
  { key: 'trigger', header: 'Why', render: (sync) => SYNC_TRIGGER_LABELS[sync.trigger] },
  { key: 'status', header: 'Result', render: (sync) => SYNC_STATUS_LABELS[sync.status] },
  {
    key: 'heads',
    header: 'Commits',
    hideOnMobile: true,
    render: (sync) =>
      sync.newHead ? (
        <span className="ri-mono">
          {shortCommit(sync.oldHead) ?? '—'} → {shortCommit(sync.newHead)}
        </span>
      ) : (
        '—'
      ),
  },
  {
    key: 'duration',
    header: 'Took',
    hideOnMobile: true,
    render: (sync) =>
      sync.durationMs === null ? '—' : `${Math.max(1, Math.round(sync.durationMs / 1000))} s`,
  },
  { key: 'error', header: 'Why it failed', render: (sync) => failureReason(sync.errorCode) ?? '' },
];

/** Sync status and history (P14-08), and "Sync now" for people who manage Repository Intelligence. */
export function SyncCard({ linkId, manage }: { linkId: string; manage: boolean }) {
  const client = useQueryClient();
  const [following, setFollowing] = useState<{ id: string; pollAfterMs: number | null } | null>(
    null,
  );
  const pollAfter = useCallback(
    (data: RepositoryIntelligenceSyncList | undefined) => {
      const sync = data?.items.find((item) => item.id === following?.id);
      if (!following || (sync && !RUNNING.has(sync.status))) {
        return null;
      }
      return following.pollAfterMs ?? undefined;
    },
    [following],
  );
  const poll = usePollWindow(following?.id ?? null, following !== null, pollAfter);
  const syncs = useRiSyncsQuery(linkId, true, poll.refetchInterval);
  const syncNow = useRequestSync(linkId);
  const keys = useSubmissionKey();
  const followedStatus = syncs.data?.items.find((item) => item.id === following?.id)?.status;
  useEffect(() => {
    if (followedStatus && !RUNNING.has(followedStatus)) {
      // Done: the status (indexed commit, freshness) changed with it, and there is nothing left to follow.
      void client.invalidateQueries({ queryKey: riKeys.status(linkId) });
      // eslint-disable-next-line react-hooks/set-state-in-effect -- the sync this follows has finished on the server.
      setFollowing(null);
    }
  }, [followedStatus, client, linkId]);

  const onSync = () => {
    if (syncNow.isPending) return;
    const clientRequestId = keys.start('sync');
    if (!clientRequestId) return;
    syncNow.mutate(
      { clientRequestId },
      keys.callbacks((receipt: { sync: { id: string; pollAfterMs: number | null } }) =>
        setFollowing({ id: receipt.sync.id, pollAfterMs: receipt.sync.pollAfterMs }),
      ),
    );
  };

  return (
    <Card
      title="Updates"
      subtitle="Repository Intelligence keeps its copy up to date by itself; a sync brings it up to date now."
      headerAddon={
        manage ? (
          <Button
            loading={syncNow.isPending || following !== null}
            disabled={syncNow.isPending}
            onClick={onSync}
          >
            Sync now
          </Button>
        ) : undefined
      }
    >
      {syncNow.isError ? <ProblemNotice error={syncNow.error} size="sm" /> : null}
      {poll.stillWorking && following ? (
        <p className="ri-muted" role="status">
          The sync is still running.{' '}
          <Button
            size="sm"
            onClick={() => {
              poll.restart();
              void syncs.refetch();
            }}
          >
            Check again
          </Button>
        </p>
      ) : null}
      {syncs.isLoading ? (
        <div aria-busy="true">
          <span className="sr-only">Loading the sync history</span>
          <SkeletonText lines={4} />
        </div>
      ) : null}
      {syncs.isError ? (
        <ProblemNotice error={syncs.error} onRetry={() => void syncs.refetch()} size="sm" />
      ) : null}
      {syncs.isSuccess ? (
        <Table
          aria-label="Sync history"
          columns={columns}
          rows={syncs.data.items}
          rowKey={(sync) => sync.id}
          density="compact"
          empty={<EmptyState size="sm" icon="○" title="No syncs yet" />}
        />
      ) : null}
    </Card>
  );
}
